import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RequestCard = ({ request }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusConfig = (status) => {
    const configs = {
      'pending': {
        color: 'bg-warning/10 text-warning border-warning/20',
        icon: 'Clock',
        label: 'Pending'
      },
      'in-progress': {
        color: 'bg-primary/10 text-primary border-primary/20',
        icon: 'Activity',
        label: 'In Progress'
      },
      'completed': {
        color: 'bg-success/10 text-success border-success/20',
        icon: 'CheckCircle2',
        label: 'Completed'
      },
      'cancelled': {
        color: 'bg-error/10 text-error border-error/20',
        icon: 'XCircle',
        label: 'Cancelled'
      }
    };
    return configs?.[status] || configs?.['pending'];
  };

  const getUrgencyConfig = (urgency) => {
    return urgency === 'Emergency'
      ? { color: 'bg-error/10 text-error border-error/20', icon: 'AlertTriangle' }
      : { color: 'bg-primary/10 text-primary border-primary/20', icon: 'Calendar' };
  };

  const getIssueTypeIcon = (issueType) => {
    const icons = {
      'Mechanical': 'Wrench',
      'Electrical': 'Zap',
      'Safety': 'Shield',
      'Routine Maintenance': 'Settings',
      'Other': 'HelpCircle'
    };
    return icons?.[issueType] || 'HelpCircle';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusConfig = getStatusConfig(request?.status);
  const urgencyConfig = getUrgencyConfig(request?.urgency);

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden hover:shadow-elevation-2 transition-smooth">
      {/* Card Header */}
      <div className="bg-muted/30 border-b border-border px-6 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name={getIssueTypeIcon(request?.issue_type)} size={20} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">{request?.request_number}</h3>
              <p className="text-xs text-muted-foreground">{formatDate(request?.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full border ${urgencyConfig?.color}`}>
              <Icon name={urgencyConfig?.icon} size={14} strokeWidth={2} />
              <span className="text-xs font-medium">{request?.urgency}</span>
            </div>
            <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full border ${statusConfig?.color}`}>
              <Icon name={statusConfig?.icon} size={14} strokeWidth={2} />
              <span className="text-xs font-medium">{statusConfig?.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-6 space-y-4">
        {/* Issue Type and Description */}
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <Icon name={getIssueTypeIcon(request?.issue_type)} size={16} color="var(--color-primary)" strokeWidth={2} />
            <span className="text-sm font-medium text-foreground">{request?.issue_type}</span>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">{request?.description}</p>
        </div>

        {/* Vehicle Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
            <Icon name="Truck" size={16} color="var(--color-primary)" strokeWidth={2} />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Unit</p>
              <p className="text-sm font-medium text-foreground truncate">{request?.unit_number}</p>
            </div>
          </div>
          {request?.vin && (
            <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
              <Icon name="Hash" size={16} color="var(--color-accent)" strokeWidth={2} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">VIN</p>
                <p className="text-xs font-mono text-foreground truncate">{request?.vin}</p>
              </div>
            </div>
          )}
          {request?.yard_name && (
            <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
              <Icon name="MapPin" size={16} color="var(--color-success)" strokeWidth={2} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Yard</p>
                <p className="text-sm font-medium text-foreground truncate">{request?.yard_name}</p>
              </div>
            </div>
          )}
        </div>

        {/* Expandable Details */}
        {isExpanded && (
          <div className="pt-4 border-t border-border space-y-4">
            {/* Full Description */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Full Description</p>
              <p className="text-sm text-foreground">{request?.description}</p>
            </div>

            {/* Parts Needed */}
            {request?.parts_needed && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Parts/Supplies Needed</p>
                <p className="text-sm text-foreground">{request?.parts_needed}</p>
              </div>
            )}

            {/* Location Info */}
            {(request?.gps_latitude || request?.gps_address) && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Location</p>
                {request?.gps_address && (
                  <p className="text-sm text-foreground mb-1">{request?.gps_address}</p>
                )}
                {request?.gps_latitude && request?.gps_longitude && (
                  <p className="text-xs text-muted-foreground font-mono">
                    {request?.gps_latitude}, {request?.gps_longitude}
                  </p>
                )}
              </div>
            )}

            {/* Photos */}
            {request?.photo_urls && request?.photo_urls?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Attached Photos ({request?.photo_urls?.length})
                </p>
                <div className="flex items-center space-x-2">
                  <Icon name="Image" size={16} color="var(--color-primary)" strokeWidth={2} />
                  <span className="text-sm text-foreground">{request?.photo_urls?.length} photo(s) attached</span>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Submitted</p>
                <p className="text-sm text-foreground">{formatDate(request?.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
                <p className="text-sm text-foreground">{formatDate(request?.updated_at)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full"
          >
            {isExpanded ? 'Show Less' : 'Show More Details'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RequestCard;